import { FamilyDto, UserRolesEnum } from '@families-link/shared';
import { FC, useCallback, useMemo } from 'react';
import { CoreTableActionsCell, CellAction } from '../../../_core/components/_ui/core-table';
import { FamiliesTableHandlers } from './families-table.component';
import { useCurrentAccessTokenSelector } from '../../../auth';
import { DeleteIcon, EditIcon, EyeIcon } from '../../../_core/components/icons';
import { useTranslation } from 'react-i18next';

export type AllowedFamilyActions = 'edit' | 'delete' | 'view';
export interface FamiliesTableActionsProps extends FamiliesTableHandlers {
	family: FamilyDto;
	allowedFamilyActions: AllowedFamilyActions[];
}

export const FamiliesTableActions: FC<FamiliesTableActionsProps> = (props) => {
	const { family, allowedFamilyActions, onDelete, onEdit, onView } = props;
	const [t] = useTranslation();
	const currentToken = useCurrentAccessTokenSelector();
	const isRequester = currentToken?.payload.sur === UserRolesEnum.Requester;

	const handleDeleteFamily = useCallback(() => {
		if (!family) {
			return;
		}

		onDelete?.(family);
	}, [onDelete, family]);

	const handleEditFamily = useCallback(() => {
		if (!family) {
			return;
		}

		onEdit?.(family);
	}, [onEdit, family]);

	const handleViewFamily = useCallback(() => {
		if (!family) {
			return;
		}

		onView?.(family);
	}, [onView, family]);

	const actions = useMemo<CellAction[]>(
		() => [
			{
				id: 'view',
				type: 'button',
				tooltipTitle: t('buttons.tooltips.full-details'),
				hidden: !allowedFamilyActions.includes('view'),
				props: {
					children: <EyeIcon sx={{ fontSize: 30 }} color="primary" />,
					onClick: handleViewFamily,
				},
			},
			{
				id: 'more',
				type: 'menu',
				props: {
					disabled: isRequester,
					hidden: !allowedFamilyActions.some(
						(el: AllowedFamilyActions) =>
							el === 'edit' || el === 'delete'
					),
					options: [
						{
							id: 'edit',
							label: 'Edit',
							icon: <EditIcon color="primary" />,
							onClick: handleEditFamily,
							disabled: isRequester,
							hidden: !allowedFamilyActions.includes('edit'),
						},
						{
							id: 'delete',
							label: 'Delete',
							icon: <DeleteIcon color="primary" />,
							onClick: handleDeleteFamily,
							disabled: isRequester,
							hidden: !allowedFamilyActions.includes('delete'),
						},
					],
				},
			},
		],
		[handleViewFamily, handleEditFamily, handleDeleteFamily]
	);

	return <CoreTableActionsCell actions={actions} />;
};
